// docHighlight.mjs — 文档页关键字高亮的纯逻辑（第一百八十批；SB-042 约定：纯逻辑入
// .mjs 配单测，DOM 挂载留在 DocBrowserPage.jsx）。
//
// 入口是 URL：`/docs/<group>/<docKey>?q=词一 词二`（q 可重复出现，值内以空白分词）。
// 站内搜索/工单/消息里指过来的链接带着「我是循着哪个词来的」，正文把这些词包成
// `<mark class="dc-hl">`，无 #hash 时滚到第一处命中。
//
// 为什么在**注入后**做而不是改 HTML 字符串：正文来自 dangerouslySetInnerHTML，
// 服务端已转义/清洗；在字符串上做替换必须自己重新解析标签边界，一个写在 href 或
// class 里的命中就会把标记污染进属性。DOM 文本节点是唯一安全的匹配面。
//
// 三条规则（都有判据）：
//   1. 只碰**文本节点**，且不进 a / code / pre（链接文字被拆成 mark 会改点击面；
//      代码块里的命中会把等宽排版切碎；已有的 mark 不再套一层——幂等）。
//   2. 大小写不敏感的**子串**匹配，不做整词——中文没有词边界，「情绪证明」必须
//      能命中「情绪证明与痕迹修复」。多词重叠时最左优先、同起点取最长（词表先按
//      长度降序）——`FR-P4` 与 `FR` 同时在表时不会只亮 `FR`。
//   3. 先收齐文本节点再改 DOM：边遍历边 replaceChild 会让 childNodes 这个活集合
//      跳过下一个兄弟节点（第一版就是这么漏掉的）。
import { safeDecode } from './docRoute.mjs';

export const HL_PARAM = 'q';
export const HL_CLASS = 'dc-hl';
// 不进这些元素：a/code/pre 是任务面要求；mark 是幂等；script/style 只是防御——
// 服务端不出裸 HTML，但这里的判据不该依赖那条契约。
export const HL_SKIP_TAGS = new Set(['A', 'CODE', 'PRE', 'MARK', 'SCRIPT', 'STYLE', 'TEXTAREA']);
// 上限是护栏不是功能：一条链接带几十个词，是有人在拼 URL，不是读者在找东西。
export const HL_MAX_TERMS = 8;
export const HL_MAX_TERM_LEN = 64;

// location.search → 词表。`q` 可重复出现（?q=a&q=b），每个值再按空白分词；
// `+` 作空格（表单编码）；解码失败原样保留（safeDecode 不抛）。
export function highlightTermsFromSearch(search) {
  const s = String(search ?? '');
  const raw = s.startsWith('?') ? s.slice(1) : s;
  const words = [];
  for (const part of raw.split('&')) {
    if (!part) continue;
    const i = part.indexOf('=');
    const k = i < 0 ? part : part.slice(0, i);
    if (safeDecode(k) !== HL_PARAM) continue;
    const v = i < 0 ? '' : safeDecode(part.slice(i + 1).replace(/\+/g, ' '));
    for (const w of v.split(/\s+/)) if (w) words.push(w);
  }
  return normalizeTerms(words);
}

// 去空、截长、大小写不敏感去重、封顶，再按长度降序（findMatches 的「同起点取最长」
// 靠这个顺序；sort 稳定，等长保留出现顺序）。
export function normalizeTerms(list) {
  const seen = new Set();
  const out = [];
  for (const raw of Array.isArray(list) ? list : []) {
    const t = String(raw ?? '').trim().slice(0, HL_MAX_TERM_LEN);
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
    if (out.length >= HL_MAX_TERMS) break;
  }
  return out.sort((a, b) => b.length - a.length);
}

// 词表的身份串：DOM 上记着「已按哪份词表高亮过」，同一份不重做，换了先清再做。
export function highlightKey(terms) {
  return (Array.isArray(terms) ? terms : []).join('\u0000');
}

// 一段文本里的命中区间 [{start,end}]，互不重叠、按位置升序。
// 大小写折叠：某些字符小写后长度会变（土耳其 İ → i̇ 是两个码元），那时索引就对不
// 上原文——退回大小写敏感，宁可少亮也不能亮错位置。
export function findMatches(text, terms) {
  const src = String(text ?? '');
  if (!src || !Array.isArray(terms) || terms.length === 0) return [];
  const lower = src.toLowerCase();
  const foldable = lower.length === src.length;
  const hay = foldable ? lower : src;
  const needles = terms.map((t) => (foldable ? t.toLowerCase() : t)).filter(Boolean);
  const out = [];
  let pos = 0;
  while (pos < hay.length) {
    let best = null;
    for (const n of needles) {
      const i = hay.indexOf(n, pos);
      if (i < 0) continue;
      if (!best || i < best.start || (i === best.start && n.length > best.end - best.start)) {
        best = { start: i, end: i + n.length };
      }
    }
    if (!best) break;
    out.push(best);
    pos = best.end;
  }
  return out;
}

// 文本 + 区间 → 片段 [{text, hit}]，空片段不出（首尾恰好命中时没有空串片段）。
export function segments(text, matches) {
  const src = String(text ?? '');
  const out = [];
  let cur = 0;
  for (const m of Array.isArray(matches) ? matches : []) {
    if (m.start > cur) out.push({ text: src.slice(cur, m.start), hit: false });
    out.push({ text: src.slice(m.start, m.end), hit: true });
    cur = m.end;
  }
  if (cur < src.length) out.push({ text: src.slice(cur), hit: false });
  return out;
}

// 深度优先收齐可高亮的文本节点（跳过 skip 里的元素整棵子树）。只用 childNodes /
// nodeType / tagName 三样，单测里用一个极小的假 DOM 就能跑。
export function collectTextNodes(root, skip = HL_SKIP_TAGS) {
  const out = [];
  const walk = (n) => {
    for (const c of Array.from(n.childNodes || [])) {
      if (c.nodeType === 3) { if (c.nodeValue) out.push(c); }
      else if (c.nodeType === 1 && !skip.has(String(c.tagName || '').toUpperCase())) walk(c);
    }
  };
  if (root) walk(root);
  return out;
}

// 把命中的文本节点拆成 [text][mark][text]…；返回生成的 mark 数量。
// doc 显式传入（而非用全局 document）是为了可测——也是为了让「这段代码需要哪些
// DOM 能力」写在签名上：createElement / createTextNode / insertBefore / removeChild。
export function applyHighlights(root, terms, doc) {
  if (!root || !doc || !Array.isArray(terms) || terms.length === 0) return 0;
  let count = 0;
  for (const tn of collectTextNodes(root)) {
    const hits = findMatches(tn.nodeValue, terms);
    if (hits.length === 0) continue;
    const parent = tn.parentNode;
    if (!parent) continue;
    for (const seg of segments(tn.nodeValue, hits)) {
      let node;
      if (seg.hit) {
        node = doc.createElement('mark');
        node.className = HL_CLASS;
        node.appendChild(doc.createTextNode(seg.text));
        count += 1;
      } else {
        node = doc.createTextNode(seg.text);
      }
      parent.insertBefore(node, tn);
    }
    parent.removeChild(tn);
  }
  return count;
}

// 反向：把我们自己包的 mark 还原成文本节点，再 normalize 合并相邻文本节点——
// 不合并的话「foo」「 bar」两个节点各自匹配，下一份词表里的 `foo bar` 就永远命中
// 不了。只认 dc-hl 类的 mark：正文里若有别处（将来的渲染器）出的 mark 不归这里管。
// 我们自己包的 mark（不深入 mark 内部——applyHighlights 从不嵌套）。
export function collectMarks(root) {
  const marks = [];
  const walk = (n) => {
    for (const c of Array.from(n.childNodes || [])) {
      if (c.nodeType !== 1) continue;
      if (String(c.tagName || '').toUpperCase() === 'MARK'
          && String(c.className || '').split(/\s+/).includes(HL_CLASS)) marks.push(c);
      else walk(c);
    }
  };
  if (root) walk(root);
  return marks;
}

export function clearHighlights(root, doc) {
  if (!root || !doc) return 0;
  const marks = collectMarks(root);
  for (const mk of marks) {
    if (!mk.parentNode) continue;
    mk.parentNode.insertBefore(doc.createTextNode(mk.textContent), mk);
    mk.parentNode.removeChild(mk);
  }
  if (typeof root.normalize === 'function') root.normalize();
  return marks.length;
}

// 正文注入后的一次同步：root 上记着上一份词表的身份（data-dc-hl）；同一份 ⇒ 不动，
// 不同 ⇒ 先清后做。返回 { changed, count }——changed 为真时调用侧才考虑滚动，
// 否则每次无关的重渲染都会把读者拽回第一处命中。
//
// 「同一份」要两证俱在：身份串相同**且**现场还有 mark。root 是跨文档常驻的那只
// Box（bodyRef），dangerouslySetInnerHTML 只换它的孩子、data-dc-hl 留着——换到
// 下一篇文档时只比身份串会把第一次高亮整个跳过（同一个 q 点两篇文档，第二篇
// 不亮）。词表为空时没有 mark 可查，身份串相同即可。
export function syncHighlights(root, terms, doc) {
  if (!root || !doc) return { changed: false, count: 0 };
  const key = highlightKey(terms);
  const prev = root.dataset ? (root.dataset.dcHl ?? '') : '';
  if (prev === key && (!key || collectMarks(root).length > 0)) return { changed: false, count: 0 };
  clearHighlights(root, doc);
  const count = applyHighlights(root, terms, doc);
  if (root.dataset) {
    if (key) root.dataset.dcHl = key;
    else delete root.dataset.dcHl;
  }
  return { changed: true, count };
}

// 滚动归属：带 #hash 时锚点效果先行（两个一起滚会打架——先跳到命中再跳到锚点，
// 闪一下），只有无 hash 且本次真的换了词表、且有命中时才滚到第一处。
export function shouldScrollToHit({ hash, changed, count }) {
  return !hash && Boolean(changed) && count > 0;
}
